import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate  } from 'react-router-dom'
import { HiOutlinePlus } from 'react-icons/hi2'
import { BiSort, BiFilter, BiX, BiDotsVerticalRounded } from 'react-icons/bi'
import { MdInventory, MdArchive, MdDelete, MdRestore } from 'react-icons/md'
import ModalAddNewInventoryItem from '../modals/ModalAddNewInventoryItem'
import ModalConfirmAction from '../modals/ModalConfirmAction'
import ModalDeleteSupplyItem from '../modals/ModalDeleteSupplyItem'
import InternalNavigationSections from '../navigation/InternalNavigationSections'
import Api from '../../common/APIUtils'
import { formatCurrencyByType, capitalizeFirstWord } from '../../common/Helpers'
import { useSubscription } from '../../context/SubscriptionContext'
import '../batches/BatchTable.css'
import './InventoryMain.css'

const SupplyMenu = ({ supply, onClose, style, libraryView, onArchive, onDelete, onRestore }) => {
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    let menuItems = []
    if (libraryView === "archived") {
        menuItems = [
            {
                label: 'Restore',
                icon: <MdRestore size={18} />,
                onClick: () => onRestore(supply)
            }
        ]
    } else {
        menuItems = [
            {
                label: 'Archive',
                icon: <MdArchive size={18} />,
                onClick: () => onArchive(supply)
            },
            {
                label: 'Delete',
                icon: <MdDelete size={18} />,
                onClick: () => onDelete(supply),
                className: 'inventory-menu-item-delete'
            }
        ]
    }

    return (
        <div className="inventory-menu" ref={menuRef} style={style}>
            {menuItems.map((item, index) => (
                <button
                    key={index}
                    className={`inventory-menu-item ${item.className || ''}`}
                    onClick={() => {
                        item.onClick();
                        onClose();
                    }}
                >
                    {item.icon}
                    <span>{item.label}</span>
                </button>
            ))}
        </div>
    );
};

export default function InventoryMain({globalState, fetchUserLibrary, dispatch}) {
    const api = new Api()
    const [query, setQuery] = useState(() => { return "" })
    const [inventoryView, setInventoryView] = useState(() => { return "all" })
    const [libraryView, setLibraryView] = useState(() => { return "all" })
    const [isAddingNewItem, setIsAddingNewItem] = useState(() => { return false })
    const [inventoryItemsRaw, setInventoryItemsRaw] = useState(() => { return [] })
    const [inventoryItemsFiltered, setInventoryItemsFiltered] = useState(() => { return [] })
    const [openMenuId, setOpenMenuId] = useState(() => { return null })
    const [selectedSupply, setSelectedSupply] = useState(() => { return null })
    const [showConfirmArchiveModal, setShowConfirmArchiveModal] = useState(() => { return false })
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(() => { return false })
    const [sortConfig, setSortConfig] = useState({
        key: 'supply_name',
        direction: 'asc'
    })
    const navigate = useNavigate();
    const { userSettings } = useSubscription()
    
    const goToPath = (path) => {
        navigate(path)
    }
    const AVAILABLE_INVENTORY = [
        { display_name: "All", view: "all" },
        { display_name: "Containers", view: "container" },
        { display_name: "Wax", view: "wax" },
        { display_name: "Fragrance Oil", view: "fragrance" },
        { display_name: "Wicks", view: "wick" },
        // { display_name: "Colors", view: "colors" },
    ]
    const closeNewCreationModal = (shouldFetch) => {
        if (shouldFetch) fetchUserLibrary()
        setIsAddingNewItem(false)
    }

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <BiSort className="sort-icon" />;
        }
        return sortConfig.direction === 'asc' ? 
            <BiSort className="sort-icon active" /> : 
            <BiSort className="sort-icon active" style={{transform: 'rotate(180deg)'}} />;
    }

    const handleArchive = (supply) => {
        setSelectedSupply(supply)
        setShowConfirmArchiveModal(true)
    }

    const handleDelete = (supply) => {
        setSelectedSupply(supply)
        setShowConfirmDeleteModal(true)
    }

    const attemptArchive = () => {
        if (!selectedSupply) return
        api.archiveSupplyItem(selectedSupply._id)
        .then(() => {
            fetchUserLibrary()
            setShowConfirmArchiveModal(false)
        })
        .catch(() => {
            setShowConfirmArchiveModal(false)
        })
    }

    const attemptRestore = (supply) => {
        if (!supply) return
        api.restoreSupplyItem(supply._id)
        .then(() => {
            fetchUserLibrary()
        })
        .catch(() => {
            return
        })
    }
    
    const updateFilteredItems = () => {
        try {
            if (!inventoryItemsRaw || inventoryItemsRaw.length === 0) return setInventoryItemsFiltered([])
            const copyOfRawInventory = Array.from(inventoryItemsRaw)
            let filteredByView = copyOfRawInventory
            if (inventoryView !== "all") {
                filteredByView = copyOfRawInventory.filter((inventoryItem) => {
                    if (inventoryItem.type === inventoryView) return true
                    return false
                })
            }
            
            // Filter by query
            let filteredItems = filteredByView
            if (query.trim()) {
                filteredItems = filteredItems.filter((item) => {
                    const itemTextToSearch = `${item.supply_name}${item.type}${item.supplier_name}${item.supplier_website}${item.notes}`
                    if (itemTextToSearch.toLowerCase().indexOf(query.trim().toLowerCase()) !== -1) return true
                    return false
                })
            }
            
            // Apply sorting
            const sortedItems = [...filteredItems].sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
            
            setInventoryItemsFiltered(sortedItems)
            
        } catch (error) {
            return setInventoryItemsFiltered([])
        }
    }
    useEffect(() => {
        if (!globalState.loaded) return
        const source = libraryView === "archived" ? globalState.archived_supplies : globalState.supplies
        setInventoryItemsRaw(source || [])
    // eslint-disable-next-line
    }, [globalState, libraryView])
    useEffect(() => {
        fetchUserLibrary()
    // eslint-disable-next-line
    }, [])
    useEffect(() => {
        updateFilteredItems()
    // eslint-disable-next-line
    }, [inventoryItemsRaw, inventoryView, query, sortConfig])

    const InventoryTableRow = ({ inventoryItem }) => {
        const buttonRef = useRef(null);

        const handleMenuClick = (e) => {
            e.stopPropagation();
            setOpenMenuId(openMenuId === inventoryItem._id ? null : inventoryItem._id);
        };

        const getMenuPosition = () => {
            if (!buttonRef.current) return {};
            const rect = buttonRef.current.getBoundingClientRect();
            return {
                position: 'fixed',
                top: `${rect.bottom + 4}px`,
                left: `${rect.left - 160}px` // Menu width is 180px
            };
        };

        return (
            <tr key={inventoryItem._id}>
                <td>
                    {libraryView === "archived" ? (
                        <span>{inventoryItem.supply_name}</span>
                    ) : (
                        <span 
                            className="batch-table-link" 
                            onClick={() => goToPath(`/candles/inventory/supplies/${inventoryItem._id}`)}
                        >
                            {inventoryItem.supply_name}
                        </span>
                    )}
                    {inventoryItem.supplier_name &&
                    <>
                    <br/>
                    <div className="recipe-table-cell-collection" style={{color: "grey"}}>
                        <span>{inventoryItem.supplier_name}</span>
                    </div>
                    </>
                    }
                </td>
                <td>{capitalizeFirstWord(inventoryItem.type)}</td>
                <td>{inventoryItem.units}</td>
                <td>{inventoryItem.quantity || 0}</td>
                <td>{formatCurrencyByType(inventoryItem.unit_price_pennies, userSettings?.inventory_units_currency)}</td>
                <td className="inventory-table-manage-cell">
                    <button 
                        ref={buttonRef}
                        className="inventory-table-manage-button"
                        onClick={handleMenuClick}
                    >
                        <BiDotsVerticalRounded size={20} />
                    </button>
                    {openMenuId === inventoryItem._id && (
                        <SupplyMenu
                            supply={inventoryItem}
                            onClose={() => setOpenMenuId(null)}
                            style={getMenuPosition()}
                            libraryView={libraryView}
                            onArchive={handleArchive}
                            onDelete={handleDelete}
                            onRestore={attemptRestore}
                        />
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div className="general-category-page-main">
            {isAddingNewItem && <ModalAddNewInventoryItem hideModal={closeNewCreationModal}/>}
            {showConfirmArchiveModal && selectedSupply &&
                <ModalConfirmAction 
                    hideModal={() => setShowConfirmArchiveModal(false)}
                    confirmButtonText="Archive item"
                    text={`Archive ${selectedSupply.supply_name}?\n\nThis will hide it from your inventory. You can restore it later from the Archived tab.`}
                    heading="⚠️ Archive Item"
                    takeActionFunction={attemptArchive}
                />
            }
            {showConfirmDeleteModal && selectedSupply &&
                <ModalDeleteSupplyItem 
                    supplyItem={selectedSupply}
                    hideModal={() => setShowConfirmDeleteModal(false)}
                    onDeleted={() => { setShowConfirmDeleteModal(false); fetchUserLibrary(); }}
                    onArchived={() => { setShowConfirmDeleteModal(false); fetchUserLibrary(); }}
                />
            }
            <div className="general-category-page-header">
                <h2>Inventory</h2>
                <div className="util-row util-align-center desktop-only" style={{gap: "12px"}}>
                    {libraryView === "all" &&
                    <div className="general-category-page-create desktop-create-button" onClick={() => setIsAddingNewItem(true)}>
                        <HiOutlinePlus/> New Item
                    </div>
                    }
                </div>
            </div>

            <InternalNavigationSections sections={["all", "archived"]} view={libraryView} setView={setLibraryView} />

            <div className="batch-table-container" style={{paddingTop: 0}}>
                <div className="inventory-controls-container">
                    <div className="inventory-search-row">
                        <div className="batch-table-search-container">
                            <input
                                type="text"
                                placeholder="Search inventory items"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="batch-table-search"
                            />
                            {query && (
                                <div className="batch-table-search-clear" onClick={() => setQuery('')}>
                                    <BiX size={20} />
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="inventory-filter-row">
                        <div className="recipe-table-collections">
                            <div className="inventory-filter-dropdown">
                                <button 
                                    className="recipe-table-filter-button" 
                                    onClick={() => {}}
                                >
                                    <BiFilter /> {AVAILABLE_INVENTORY.find(item => item.view === inventoryView)?.display_name || 'All'}
                                </button>
                                <select 
                                    className="inventory-select-filter" 
                                    onChange={(e) => setInventoryView(e.target.value)} 
                                    value={inventoryView}
                                >
                                    {AVAILABLE_INVENTORY.map((inventory_item) => (
                                        <option value={inventory_item.view} key={inventory_item.view}>{inventory_item.display_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        {libraryView === "all" &&
                        <div className="mobile-only inventory-mobile-create">
                            <div className="general-category-page-create mobile-create-button" onClick={() => setIsAddingNewItem(true)}>
                                <HiOutlinePlus size={20}/>
                            </div>
                        </div>
                        }
                    </div>
                </div>

                <div className="batch-table-wrapper">
                    <table className="batch-table">
                        <thead>
                            <tr>
                                <th>
                                    <div className="table-header-content active" onClick={() => handleSort('supply_name')}>
                                        Name {renderSortIcon('supply_name')}
                                    </div>
                                </th>
                                <th>
                                    <div className="table-header-content" onClick={() => handleSort('type')}>
                                        Type {renderSortIcon('type')}
                                    </div>
                                </th>
                                <th>
                                    <div className="table-header-content">Units</div>
                                </th>
                                <th>
                                    <div className="table-header-content" onClick={() => handleSort('quantity')}>
                                        Quantity {renderSortIcon('quantity')}
                                    </div>
                                </th>
                                <th>
                                    <div className="table-header-content" onClick={() => handleSort('unit_price_pennies')}>
                                        Unit Price {renderSortIcon('unit_price_pennies')}
                                    </div>
                                </th>
                                <th className="inventory-table-manage-header">
                                    <div className="table-header-content">Manage</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {inventoryItemsFiltered.map((inventoryItem) => (
                                <InventoryTableRow key={inventoryItem._id} inventoryItem={inventoryItem} />
                            ))}
                        </tbody>
                    </table>
                </div>
                
                {inventoryItemsFiltered.length === 0 && !globalState.loaded &&
                <div className="batch-table-loading">Loading...</div>
                }
                
                {inventoryItemsFiltered.length === 0 && globalState.loaded && libraryView === "archived" && !query &&
                <div className="batch-table-empty">
                    <MdInventory className="batch-table-empty-icon"/>
                    <h3>The archive is empty</h3>
                    <p>Archived inventory items will appear here</p>
                </div>
                }

                {inventoryItemsFiltered.length === 0 && globalState.loaded && (libraryView === "all" || query) &&
                <div className="batch-table-empty">
                    <MdInventory className="batch-table-empty-icon"/>
                    <h3>No inventory items found</h3>
                    {query ? (
                        <p>Try adjusting your search or filters</p>
                    ) : (
                        <p>Add your first inventory item to get started</p>
                    )}
                    {query && (
                        <button 
                            onClick={() => {
                                setQuery('');
                                setInventoryView('all');
                            }}
                            className="batch-table-clear-filters"
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                }
            </div>
        </div>
    )
}
